import store from "../../../app/store";
import { Axios } from "../../../services/axios";
import { kycDocsId } from "../utils";
import { IRemittanceAPIProps } from "./IRemittanceAPI";

export const getAuthData = () => {
  const { auth, remittance } = store.getState();
  return {
    token: auth.currentUser?.idToken || null,
    vendorKey: auth.currentUser?.vendor_key || null,
    userKey: auth.userKey || auth.currentUser?.vendor_key || null,
    vendorDropdownSelected: remittance?.calculator?.amountCalculation?.vendor_key || null,
    partner_type: auth.currentUser?.partner_type || "",
  };
};

export const remittanceApi = {
  async getPurposeList() {
    const { token } = getAuthData();
    return await Axios.get(`/remit/auth/user/purposeCode?platform=WEB`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getLinkedVendorCode() {
    const { token, vendorKey } = getAuthData();
    return await Axios.get(`/remit/user/${vendorKey}/linkedVendorCode`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getCardRate() {
    const { token } = getAuthData();
    return await Axios.get(`/remit/fxRates/card-rate`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getFxRates(dest_currency: string) {
    const { token, vendorKey } = getAuthData();
    // const vendorDropdown = vendorDropdownSelected ? vendorDropdownSelected : vendorKey;
    const vendorDropdown = vendorKey;
    return await Axios.get(
      `/remit/fxRates/${vendorDropdown}/latest?base=inr&symbols=${dest_currency}`,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async getTransactionSummary(formData: any) {
    const { token } = getAuthData();
    return await Axios.post("/remit/user/getTransactionSummary", formData, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getLockBookRate(formData: IRemittanceAPIProps.ILockBookRateProps) {
    const { token } = getAuthData();
    return await Axios.post("/remit/user/rate/book", formData, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getRemitterList(formData: IRemittanceAPIProps.IRemitterListPayload) {
    const { token, vendorKey } = getAuthData();
    const { remittance } = store.getState();
    const transactionVendor = remittance?.calculator?.amountCalculation?.transVendorKey ?? "";

    return await Axios.get(`/remit/auth/user/${vendorKey}/getUserRegDetails`, {
      headers: {
        Authorization: token,
      },
      params: {
        ...formData,
        transactionVendor,
      },
    });
  },

  /** AD-II master/sub vendor feature config for logged-in + transaction vendor. */
  async getAdIIVendorConfig(transactionVendorKey: string) {
    const { token } = getAuthData();
    return await Axios.get(`/remit/kyc/ad-ii-config/${transactionVendorKey}`, {
      headers: {
        Authorization: token,
      },
    });
  },

  async getRemitterResendKycLink(remitterId: string) {
    const { token } = getAuthData();
    return await Axios.get(`/remit/kyc/link/${remitterId}`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getDeleteRemitter(remitterId: string) {
    const { token } = getAuthData();
    return await Axios.delete(`/remit/auth/vendor-user/${remitterId}`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getAddRemitterSendOtp(body: IRemittanceAPIProps.IAddRemitterSendOtp) {
    const { partner_type } = getAuthData();
    return await Axios.post(`/remit/auth/sendMobileOtp`, body, {
      headers: {
        "partner-type": partner_type,
      },
    });
  },
  async getAddRemitterValidateOtp(body: IRemittanceAPIProps.IAddRemitterValidateOtp) {
    const { partner_type } = getAuthData();
    return await Axios.post(`/remit/auth/validateOtp`, body, {
      headers: {
        "partner-type": partner_type,
      },
    });
  },
  async getAddRemitterSignup(body: IRemittanceAPIProps.IAddRemitterSignup) {
    const { token, vendorKey } = getAuthData();
    return await Axios.post(`/remit/auth/user/${vendorKey}/signup`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getRemitterCkycStatus(remitterId: string) {
    const { token } = getAuthData();
    return await Axios.get(`/remit/kyc/ckyc/status/${remitterId}`, {
      headers: {
        Authorization: token,
      },
    });
  },

  /** AD-II new/re-KYC: CKYC search before creating vKYC profile. */
  async searchAdIICkyc(payload: {
    userKey: string;
    requestType?: string;
    loggedInUserKey: string;
    panNumber?: string;
    name?: string;
    dob?: string;
  }) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/kyc/ckyc/search`,
      {
        userKey: payload.userKey,
        requestType: payload.requestType ?? "k2",
        loggedInUserKey: payload.loggedInUserKey,
        ...(payload.panNumber ? { panNumber: payload.panNumber } : {}),
        ...(payload.name ? { name: payload.name } : {}),
        ...(payload.dob ? { dob: payload.dob } : {}),
      },
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },

  /** AD-II new/re-KYC: create vKYC profile and send link (sms | email | both). */
  async createAdIIVkycProfile(payload: {
    journeyId: number;
    userKey: string;
    requestType?: string;
    loggedInUserKey?: string;
    sendType: "sms" | "email" | "both";
  }) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/kyc/vkyc/create-profile`,
      {
        journeyId: payload.journeyId,
        userKey: payload.userKey,
        requestType: payload.requestType ?? "k2",
        sendType: payload.sendType,
        ...(payload.loggedInUserKey ? { loggedInUserKey: payload.loggedInUserKey } : {}),
      },
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },

  async generateAdIIVkycNewLink(payload: {
    uniqueKey: string;
    sendType: "sms" | "email" | "both";
  }) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/kyc/vkyc/generate-new-link`,
      {
        uniqueKey: payload.uniqueKey,
        sendType: payload.sendType,
      },
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },

  async resendAdIIVkycLink(payload: { userKey: string; uniqueKey?: string; userId?: string }) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/kyc/vkyc/resend-link`,
      {
        userKey: payload.userKey,
        ...(payload.uniqueKey ? { uniqueKey: payload.uniqueKey } : {}),
        ...(payload.userId ? { userId: payload.userId } : {}),
      },
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },

  async getRemitterDocuments(remitterId: string) {
    const { token } = getAuthData();
    return await Axios.get(`/remit/users/${remitterId}/documents`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getRecipientsByRemitter(body: IRemittanceAPIProps.IRecipientListPayload) {
    const { token } = getAuthData();
    const { page, size, ascending, filter, purposeCode, currency, remitterId } = body;
    return await Axios.get(`/remit/user/${remitterId}/listOfRecipients`, {
      headers: {
        Authorization: token,
      },
      params: {
        page: page,
        size: size,
        ascending: ascending,
        filter: filter,
        purposeCode: purposeCode,
        currency: currency,
      },
    });
  },
  async getRecipientDetailByRemitter(body: IRemittanceAPIProps.IRecipientDetailPayload) {
    const { token } = getAuthData();
    const { remitterId, recipientId } = body;
    return await Axios.get(`/remit/user/${remitterId}/recipients/${recipientId}`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getDeleteRecipientByRemitter(body: IRemittanceAPIProps.IRecipientDetailPayload) {
    const { token } = getAuthData();
    const { remitterId, recipientId } = body;
    return await Axios.delete(`/remit/user/${remitterId}/recipients/${recipientId}`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getDocumentsList(formData: IRemittanceAPIProps.IDocumentsListPayload) {
    const { token } = getAuthData();
    return await Axios.get(`/remit/user/recipient/docs`, {
      headers: {
        Authorization: token,
      },
      params: formData,
    });
  },
  async getLeadDocumentsList(formData: IRemittanceAPIProps.ILeadDocumentsListPayload) {
    const { token } = getAuthData();
    const { purposeCode, remitterId, recipientId } = formData;
    return await Axios.get(
      `remit/user/${remitterId}/lead/upload/doc/${recipientId}?purposeCode=${purposeCode}`,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async uploadRecipientDocs(formData: IRemittanceAPIProps.IUploadRecipientDocs) {
    const { token } = getAuthData();
    const { remitterId, file, docCode, transactionPurpose } = formData;
    return await Axios.post(
      `/remit/user/${remitterId}/lead/upload/doc?docType=${docCode}&purpose=${transactionPurpose}`,
      file,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async getRecipientDocsDownload(docId: string) {
    const { token } = getAuthData();
    return await Axios.get(`remit/user/${token}/recipient/docId/${docId}`, {
      responseType: "blob",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });
  },
  async getRemitterSignedStatus(remitterId: string) {
    const { token } = getAuthData();
    return await Axios.get(`remit/user/signedStatus/user/${remitterId}`, {
      headers: {
        "Access-Control-Allow-Origin": "true",
        "Content-Type": "application/json",
        Authorization: token,
      },
    });
  },
  async getModalContent(pageName: string) {
    const { token } = getAuthData();
    return await Axios.get(`/remit/modal/content/get?pageName=${pageName}`, {
      headers: {
        Authorization: token,
        "Access-Control-Allow-Origin": "true",
        "Content-Type": "application/json",
      },
    });
  },
  async getViewA2form(remitterId: string, params: any) {
    const { token } = getAuthData();
    return await Axios.get(`/remit/user/${remitterId}/generateA2Form/?${params.toString()}`, {
      headers: {
        Authorization: token,
        "Access-Control-Allow-Origin": "true",
        "Content-Type": "application/json",
      },
      responseType: "blob",
    });
  },
  async remitterChangeStatusLater(remitterId: string) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/auth/signature/flag/doLater/${remitterId}`,
      { userId: remitterId },
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async sendRemitterEsignLinkToSms(body: IRemittanceAPIProps.SendRemitterEsignLinkToSMSProps) {
    const { token } = getAuthData();
    return await Axios.post(`/remit/auth/sendA2FromLink`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async searchQuoteDetails(body: IRemittanceAPIProps.SearchQuoteDetailsPayload) {
    const { token, userKey } = getAuthData();
    return await Axios.post(`/remit/user/${userKey}/searchQuoteDetails`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async initializeTransaction(
    remitterId: string,
    body: IRemittanceAPIProps.InitializeTransactionProps
  ) {
    const { token } = getAuthData();
    return await Axios.post(`/remit/user/${remitterId}/initializeTransaction`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getNeftRtgsBankDetails(body: IRemittanceAPIProps.INeftRtgsPayload) {
    const { token, vendorKey } = getAuthData();
    const { ad1Transaction, referrerVendorCode, purposeCode, purposeName } = body;
    return await Axios.get(`/remit/user/${vendorKey}/neftBankDetails`, {
      headers: {
        Authorization: token,
      },
      params: {
        ad1Transaction,
        referrerVendorCode,
        purposeCode,
        purposeName,
      },
    });
  },
  async getShareAccountDetailsByTxn(transactionNumber: string) {
    const { token, vendorKey } = getAuthData();
    return await Axios.get(`/remit/user/${vendorKey}/shareAccountDetails/${transactionNumber}`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getNetBankingList() {
    const { token } = getAuthData();
    return await Axios.get(`/remit/bank/fetch/net-banks`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async postPennyDrop(body: IRemittanceAPIProps.IPennyDropPayload) {
    const { token } = getAuthData();
    return await Axios.post(`/remit/bank/pennydrop/razorpay`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async postRequestRazorPayLink(body: IRemittanceAPIProps.IRequestRazorPayLinkPayload) {
    const { token } = getAuthData();
    return await Axios.post(`/remit/bank/payment/requestRazorLink`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async postRequestRazor(body: IRemittanceAPIProps.IRequestRazorPayLinkPayload) {
    const { token } = getAuthData();
    return await Axios.post(`/remit/bank/payment/requestRazor`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async postRazorPayResponse(body: IRemittanceAPIProps.IRazorPayResponsePayload) {
    const { token } = getAuthData();
    return await Axios.post(`/remit/bank/payment/razorPayResponse`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getRecipientCategoryList(formData: IRemittanceAPIProps.IRecipientCategoryListParams) {
    const { token, vendorKey } = getAuthData();
    return await Axios.get(`/remit/user/${vendorKey}/validRecipientsType`, {
      headers: {
        Authorization: token,
      },
      params: formData,
    });
  },
  async getValidRecipientSwiftOrIban(type: string, number: string) {
    const { token } = getAuthData();
    return await Axios.get(`remit/user/validateRecipient?typeOfAccount=${type}&number=${number}`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async postAddRecipientByRemitter(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(`/remit/user/${remitterId}/recipients`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async getRemitterDetails(remitterId: string) {
    const { token } = getAuthData();
    return await Axios.get(`/remit/auth/user/${remitterId}`, {
      headers: {
        Authorization: token,
      },
    });
  },
  async updateRemitterDetails(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.put(`/remit/auth/user/${remitterId}`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async uploadRemitterDocuments(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(`/remit/users/${remitterId}/senders/1/documents`, body, {
      headers: {
        Authorization: token,
      },
    });
  },
  async uploadRemitterAadharBack(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.put(
      `/remit/users/${remitterId}/documents//${kycDocsId.UPLOAD_AADHAR_BACK}`,
      body,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async remitterVerifyPAN(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/users/${remitterId}/documents/${kycDocsId.E_VERIFY_PAN}/verifyPan`,
      body,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async remitterVerifyAadhar(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/users/${remitterId}/documents/${kycDocsId.VERIFY_AADHAR_CARD}/verifyDoc`,
      body,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async remitterVerifyPassportFront(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/users/${remitterId}/documents/${kycDocsId.PASSPORT_FRONT}/verifyDoc`,
      body,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async remitterVerifyPassportBack(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/users/${remitterId}/documents/${kycDocsId.PASSPORT_BACK}/verifyPassport`,
      body,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async remitterVerifyDL(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/users/${remitterId}/documents/${kycDocsId.E_VERIFY_DL}/verifyDL`,
      body,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async remitterVerifyDLLDocument(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/users/${remitterId}/documents/${kycDocsId.VERIFY_DL_DOC}/verifyDocument`,
      body,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async remitterVerifyVoterID(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/users/${remitterId}/documents/${kycDocsId.E_VERIFY_VOTERID}/verifyVoterID`,
      body,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
  async remitterVerifyVoterIDLDocument(remitterId: string, body: any) {
    const { token } = getAuthData();
    return await Axios.post(
      `/remit/users/${remitterId}/documents/${kycDocsId.VERIFY_VOTERID_DOC}/verifyDocument`,
      body,
      {
        headers: {
          Authorization: token,
        },
      }
    );
  },
};
